import { createTheme } from "@mui/material/styles";
const LightMode = {
    brandColors: {
        4: "#FFF9F9",
        3: "#FBECEC",
        2: "#D63838",
        1: "#C61A1B",
        0: "#8F0808",
        "-1": "#4F0E13",
        "-2": "#230808",
    },
    notifyingColors: {
        inactive: "#737373",
        error: "#DE3702",
        complete: "#00855D",
        active: "#4F3838",
        success: "#227700",
        info: "#005599",
        warning: "#9F6000",
        critical: "#D8000C",
    },
    specialColors: {
        popupBackground: null,
        popupText: null,
        searchBarBackground: "#F5F5F5",
        greyButGray: "#949494",
        passwordStrengthChipIncomplete: "#D9D9D9",
        passwordStrengthChipComplete: "#C75300",
        passwordStrengthAllChipsComplete: "#00855D",
    },
    background: "#FFFFFF",
    arrowBackground: "#F5F5F5",
    layoutButtonBackground: "#F5F5F5",
};
const DarkMode = {
    brandColors: {
        4: "#FFF9F9",
        3: "#FBECEC",
        2: "#FFA6A6",
        1: "#FF6666",
        0: "#F32020",
        "-1": "#4F0E13",
        "-2": "#230808",
    },
    notifyingColors: {
        inactive: "#7D7D7D",
        error: "#FF5F3C",
        complete: "#00D696",
        active: "#4F3838",
        success: "#227700",
        info: "#005599",
        warning: "#9F6000",
        critical: "#D8000C",
    },
    specialColors: {
        // https://bryx.slack.com/archives/C03L34EMC5B/p1662657944582809
        popupBackground: "#463434",
        popupText: "#A1A1A1",
        searchBarBackground: "#463434",
        greyButGray: "#949494",
        passwordStrengthChipIncomplete: "#D9D9D9",
        passwordStrengthChipComplete: "#C75300",
        passwordStrengthAllChipsComplete: "#00855D",
    },
    background: "#230808",
    arrowBackground: "#F5F5F5",
    layoutButtonBackground: "#F5F5F5",
};
const BryxColors = {
    LightMode,
    DarkMode,
    darkBrand(key) {
        if (key < 5 && key > -3) {
            return DarkMode.brandColors[key];
        }
        return DarkMode.brandColors["0"];
    },
    lightBrand(key) {
        if (key < 5 && key > -3) {
            return LightMode.brandColors[key];
        }
        return LightMode.brandColors["0"];
    },
    brand(theme, key) {
        return theme == "dark" ? this.darkBrand(key) : this.lightBrand(key);
    },
    brandPrimary(theme) {
        return this.brand(theme, 0);
    },
    brand1(theme) {
        return this.brand(theme, 1);
    },
    brand2(theme) {
        return this.brand(theme, 2);
    },
    brand3(theme) {
        return this.brand(theme, 3);
    },
    brand4(theme) {
        return this.brand(theme, 4);
    },
    brandDark1(theme) {
        return this.brand(theme, -1);
    },
    brandDark2(theme) {
        return this.brand(theme, -2);
    },
    darkNotify(key) {
        return DarkMode.notifyingColors[key];
    },
    lightNotify(key) {
        return LightMode.notifyingColors[key];
    },
    notify(theme, key) {
        return theme == "dark" ? this.darkNotify(key) : this.lightNotify(key);
    },
    background(theme) {
        return theme == "dark" ? DarkMode.background : LightMode.background;
    },
    arrowBackground(theme) {
        return theme == "dark" ? DarkMode.arrowBackground : LightMode.arrowBackground;
    },
    layoutButtonBackground(theme) {
        return theme == "dark" ? DarkMode.layoutButtonBackground : LightMode.layoutButtonBackground;
    },
    darkModePopupBackground() {
        return DarkMode.specialColors.popupBackground;
    },
    darkModePopupText() {
        return DarkMode.specialColors.popupText;
    },
    specialColor(theme) {
        return theme == "dark" ? DarkMode.specialColors : LightMode.specialColors;
    },
};
export function constructMuiTheme(mode) {
    return createTheme({
        typography: {
            fontFamily: "Open Sans",
        },
        palette: {
            mode,
            ...(mode === "light"
                ? {
                    // palette values for light mode
                    background: {
                        default: "#FFF",
                        paper: "#FFF",
                    },
                    text: {
                        primary: BryxColors.brandDark2("dark"),
                        secondary: BryxColors.brandDark1("dark"),
                    },
                }
                : {
                    // palette values for dark mode
                    background: {
                        default: BryxColors.background("dark"),
                        paper: BryxColors.background("dark"),
                    },
                    text: {
                        primary: BryxColors.brand4("dark"),
                        secondary: BryxColors.brand3("dark"),
                    },
                }),
        },
        components: {
            MuiButtonBase: {
                defaultProps: {
                    disableRipple: true,
                },
            },
            MuiCalendarPicker: {
                defaultProps: {
                    showDaysOutsideCurrentMonth: true,
                    reduceAnimations: true,
                },
                styleOverrides: {
                    root: {
                        "& .PrivatePickersSlideTransition-root": {
                            minHeight: "210px", // originally 240px, adds padding
                        },
                        '& > div > div[aria-live="polite"] > div': {
                            // this is the title with month and year, e.g. September 2022
                            // there is no class name for it so we have to do this :(
                            fontSize: "16px",
                            fontWeight: 700, // 700 is from figma
                        },
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: {
                        "& .MuiTypography-root": {
                            color: BryxColors.brandDark2("dark"),
                        },
                    },
                },
            },
            MuiList: {
                styleOverrides: {
                    root: {
                        padding: "4px 2px !important",
                        "& li": {
                            borderRadius: "4px",
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        boxShadow: "0px 7px 20px 0px rgb(0 0 0 / 14%), 0px 7px 10px -10px rgb(0 0 0 / 12%)",
                        "&:has(.MuiList-root)": {
                            transform: "translateY(10px) !important",
                        },
                    },
                },
            },
            MuiPickersDay: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        // NB: this is for days OUTSIDE the month when showing days that are outside the month
                        // as in, even though the below class says outside month, it is for days that are inside the month
                        // the root element will render for days that are ACTUALLY outside the month
                        // because we default to showDaysOutsideCurrentMonth = true above in MuiCalendarPicker
                        color: BryxColors.notify(mode, "inactive"),
                        backgroundImage: "linear-gradient(#ffffff1f, #ffffff1f)",
                        "&:hover": {
                            backgroundColor: `${BryxColors.brand3(mode)} !important`,
                            borderRadius: "4px",
                            color: `${BryxColors.brandPrimary(mode)} !important`,
                        },
                        fontSize: "16px",
                        "&.Mui-selected": {
                            border: `1px solid ${BryxColors.brandPrimary(mode)}`,
                            borderRadius: "4px",
                            backgroundColor: `${BryxColors.brandPrimary(mode)} !important`,
                            "&:hover": {
                                border: `1px solid ${BryxColors.brand3(mode)}`,
                                backgroundColor: `${BryxColors.brand3(mode)} !important`,
                                borderRadius: "4px",
                                color: BryxColors.brandPrimary(mode),
                            },
                            "&:focus": {
                                backgroundColor: BryxColors.brandPrimary(mode),
                                borderRadius: "4px",
                                color: BryxColors.brand4("dark"),
                            },
                        },
                        "&:focus": {
                            backgroundColor: "transparent",
                        },
                    },
                    dayOutsideMonth: {
                        // NB: this is for days INSIDE the month when also showing days that are outside the month
                        // as in, even though this says outside month, this is for days that are inside the month
                        // the root element will render for days that are ACTUALLY outside the month
                        // because we default to showDaysOutsideCurrentMonth = true above in MuiCalendarPicker
                        color: mode == "light" ? BryxColors.brandDark2("dark") : BryxColors.brand4("dark"),
                        backgroundColor: mode === "dark" ? BryxColors.brandDark2(mode) : undefined,
                        "&:hover": {
                            backgroundColor: BryxColors.brandPrimary(mode),
                            borderRadius: "4px",
                            color: BryxColors.brand4("dark"),
                        },
                        fontSize: "13px",
                        "&.Mui-selected": {
                            border: `1px solid ${BryxColors.brandPrimary(mode)}`,
                            borderRadius: "4px",
                            backgroundColor: BryxColors.brandPrimary(mode),
                            "&:hover, &:focus": {
                                backgroundColor: BryxColors.brandPrimary(mode),
                                borderRadius: "4px",
                                color: BryxColors.brand4("dark"),
                            },
                        },
                    },
                    today: {
                        border: `1px solid ${BryxColors.brandPrimary(mode)}`,
                        borderRadius: "4px",
                        "&:not(.Mui-selected)": {
                            border: `1px solid ${BryxColors.brandPrimary(mode)}`,
                            borderRadius: "4px",
                        },
                        "&:hover": {
                            backgroundColor: BryxColors.brandPrimary(mode),
                            borderRadius: "4px",
                            color: BryxColors.brand4("dark"),
                        },
                        fontSize: "13px",
                        backgroundColor: mode === "dark" ? BryxColors.brandDark2(mode) : BryxColors.background(mode),
                        "&:focus": {
                            backgroundColor: mode === "dark" ? BryxColors.brandDark2(mode) : BryxColors.background(mode),
                        },
                    },
                },
            },
            MuiMonthPicker: {
                defaultProps: {
                    sx: {
                        "& .MuiTypography-root.Mui-selected, &:focus": {
                            borderRadius: "4px",
                            backgroundColor: BryxColors.brandPrimary(mode),
                            "&:hover": {
                                backgroundColor: BryxColors.brand1(mode),
                            },
                        },
                    },
                },
            },
            MuiSelect: {
                defaultProps: {
                    MenuProps: {
                        PaperProps: {
                            sx: {
                                backgroundColor: mode === "light" ? BryxColors.background(mode) : BryxColors.darkModePopupBackground(),
                                color: mode === "light" ? BryxColors.notify(mode, "inactive") : BryxColors.darkModePopupText(),
                                borderRadius: "4px",
                            },
                        },
                        MenuListProps: {
                            sx: {
                                padding: "2px",
                            },
                        },
                    },
                },
            },
            MuiMenuItem: {
                defaultProps: {
                    disableRipple: true,
                    sx: {
                        ":hover": {
                            backgroundColor: mode === "light" ? BryxColors.brand3(mode) : BryxColors.brandDark1(mode),
                            color: mode === "light" ? BryxColors.brandPrimary(mode) : BryxColors.brand2(mode),
                        },
                        ":focus:not(:hover, .Mui-selected)": {
                            backgroundColor: mode === "light" ? BryxColors.background(mode) : BryxColors.darkModePopupBackground(),
                            color: mode === "light" ? BryxColors.notify(mode, "inactive") : BryxColors.darkModePopupText(),
                        },
                        "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
                            backgroundColor: mode === "light" ? BryxColors.brand3(mode) : BryxColors.brandDark1(mode),
                            color: mode === "light" ? BryxColors.brandPrimary(mode) : BryxColors.brand2(mode),
                        },
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    arrow: {
                        "::before": {
                            borderRadius: "1.5px",
                            backgroundColor: mode == "light" ? "#fff" : BryxColors.darkModePopupBackground(),
                        },
                        top: "3px !important",
                    },
                    tooltipArrow: {
                        "::before": {
                            borderRadius: "1.5px",
                            backgroundColor: mode == "light" ? "#fff" : BryxColors.darkModePopupBackground(),
                        },
                        top: "3px !important",
                    },
                    tooltip: {
                        backgroundColor: mode == "light" ? "#fff" : BryxColors.darkModePopupBackground(),
                        color: mode == "light" ? BryxColors.brandDark2(mode) : BryxColors.brand4(mode),
                        marginTop: "-2.15rem",
                        marginLeft: "4px !important",
                        // copied from the time picker popper shadow
                        boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)",
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        color: BryxColors.brandDark2(mode),
                        fontWeight: 400,
                        fontSize: "16px",
                        textTransform: "none",
                        "&.Mui-selected": {
                            color: BryxColors.brandPrimary(mode),
                            fontWeight: 800,
                        },
                        minHeight: "22px",
                        minWidth: "70px",
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: BryxColors.brandPrimary(mode),
                        height: "3px",
                        borderRadius: "4px",
                    },
                },
            },
        },
    });
}
export default BryxColors;
